/**
 * Visit Notes Repository (Driven Adapter)
 *
 * Makes HTTP calls to the unified /visit-notes API endpoints
 */

import type { IAPIProvider } from "../../../core/ports/driven/apiProvider";
import type { IAuthService } from "../../../core/ports/driven/authService";
import type { IAPIRoutes } from "../../../../apiRoutes";
import type {
  IVisitNotesRepository,
  ICreateVisitNoteRequest,
  IUpdateVisitNoteRequest,
  IVisitNote,
} from "../../../core/ports/driver/VisitNotesService";

export type IVisitNotesRepositoryType = ReturnType<
  typeof VisitNotesRepositoryFactory
>;

export const VisitNotesRepositoryFactory = ({
  APIProvider,
  AuthService,
  APIRoutes,
}: {
  APIProvider: IAPIProvider;
  AuthService: IAuthService;
  APIRoutes: IAPIRoutes;
}): IVisitNotesRepository => {
  const { POST, PATCH } = APIProvider;
  const { getAuthenticationToken } = AuthService;

  /**
   * Create a new visit note
   * POST /clinic/{clinic_id}/patient/{patient_id}/visit-notes
   */
  const createVisitNoteAPI = async (
    request: ICreateVisitNoteRequest
  ): Promise<IVisitNote> => {
    const url = APIRoutes.apiCreateVisitNote
      .replace("{clinic_id}", request.clinic_id.toString())
      .replace("{patient_id}", request.patient_id.toString());

    const payload = {
      clinic_id: request.clinic_id,
      patient_id: request.patient_id,
      doc_type: request.doc_type,
      type: request.type,
      notes: request.notes,
      file_link: request.file_link,
      total_time: request.total_time,
      context: request.context,
      ...(request.shortlisted !== undefined && {
        shortlisted: request.shortlisted,
      }),
    };

    const response = await POST(url, payload, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });

    return response.data || response;
  };

  /**
   * Update an existing visit note
   * PATCH /clinic/{clinic_id}/patient/{patient_id}/visit-notes/{note_id}
   */
  const updateVisitNoteAPI = async (
    clinicId: number,
    patientId: number,
    noteId: number,
    request: IUpdateVisitNoteRequest
  ): Promise<IVisitNote> => {
    const url = APIRoutes.apiUpdateVisitNote
      .replace("{clinic_id}", clinicId.toString())
      .replace("{patient_id}", patientId.toString())
      .replace("{note_id}", noteId.toString());

    // Include shortlisted if provided
    const updatePayload = {
      ...request,
      ...(request.shortlisted !== undefined && {
        shortlisted: request.shortlisted,
      }),
    };

    const response = await PATCH(url, updatePayload, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });

    return response.data || response;
  };

  return {
    createVisitNoteAPI,
    updateVisitNoteAPI,
  };
};
